import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FaArrowDown, FaArrowUp, FaChartPie } from 'react-icons/fa';
import ScreenContainer from '../components/ScreenContainer';
import AddPortfolioTradeFlow from '../components/AddPortfolioTradeFlow';
import { Account, PortfolioSummary, PortfolioTrade } from '../types';
import { fetchPortfolioSummary, fetchPortfolioTrades } from '../services/apiService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAccounts } from '../store/slices/accountsSlice';
import { formatCurrency, formatDate } from '../utils/formatters';

interface PortfolioAccountDetailsRouteState {
  account?: Account;
}

const getReturnPercent = (summary: PortfolioSummary) => {
  if (!summary.totalInvested) {
    return 0;
  }
  return (summary.totalProfitLoss / summary.totalInvested) * 100;
};

function PortfolioAccountDetailsScreen() {
  const dispatch = useAppDispatch();
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const routeState = location.state as PortfolioAccountDetailsRouteState | null;
  const fallbackAccount = routeState?.account;
  const parsedAccountId = Number(accountId);
  const resolvedAccountId = Number.isFinite(parsedAccountId) ? parsedAccountId : fallbackAccount?.id;
  const { accounts, status: accountsStatus } = useAppSelector((state) => state.accounts);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [trades, setTrades] = useState<PortfolioTrade[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'succeeded' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
  }, [accountsStatus, dispatch]);

  const account = useMemo(() => {
    if (!resolvedAccountId) {
      return fallbackAccount ?? null;
    }
    return accounts.find((candidate) => candidate.id === resolvedAccountId) ?? fallbackAccount ?? null;
  }, [accounts, fallbackAccount, resolvedAccountId]);

  const loadPortfolioAccount = useCallback(async (isCancelled?: () => boolean) => {
    if (!resolvedAccountId) {
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const [nextSummary, nextTrades] = await Promise.all([
        fetchPortfolioSummary(resolvedAccountId),
        fetchPortfolioTrades(resolvedAccountId),
      ]);
      if (isCancelled?.()) {
        return;
      }
      setSummary(nextSummary);
      setTrades(nextTrades);
      setStatus('succeeded');
    } catch (caughtError) {
      if (isCancelled?.()) {
        return;
      }
      setStatus('failed');
      setError(caughtError instanceof Error ? caughtError.message : 'Failed to load portfolio account.');
    }
  }, [resolvedAccountId]);

  useEffect(() => {
    let isCancelled = false;
    loadPortfolioAccount(() => isCancelled);

    return () => {
      isCancelled = true;
    };
  }, [loadPortfolioAccount]);

  const returnPercent = summary ? getReturnPercent(summary) : 0;

  return (
    <ScreenContainer
      title={account?.name ?? 'Portfolio Account'}
      headerAction={account && resolvedAccountId ? (
        <AddPortfolioTradeFlow
          account={account}
          holdings={summary?.holdings ?? []}
          onTradeCreated={() => loadPortfolioAccount()}
        />
      ) : undefined}
    >
      <div className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 pb-8 pt-2">
        {status === 'loading' && (
          <div className="rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground">
            Loading portfolio account...
          </div>
        )}

        {status === 'failed' && (
          <div className="rounded-2xl border border-border bg-card p-6 text-center text-error">
            {error}
          </div>
        )}

        {summary && (
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Current value</div>
                <div className="mt-1 text-4xl font-semibold">{formatCurrency(summary.currentValue)}</div>
              </div>
              <div className={`rounded-2xl p-3 ${summary.totalProfitLoss >= 0 ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}>
                <FaChartPie className="h-6 w-6" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Metric label="Invested" value={formatCurrency(summary.totalInvested)} />
              <Metric label="Total P/L" value={`${summary.totalProfitLoss >= 0 ? '+' : ''}${formatCurrency(summary.totalProfitLoss)}`} tone={summary.totalProfitLoss >= 0 ? 'positive' : 'negative'} />
              <Metric label="Realised P/L" value={`${summary.realisedProfitLoss >= 0 ? '+' : ''}${formatCurrency(summary.realisedProfitLoss)}`} tone={summary.realisedProfitLoss >= 0 ? 'positive' : 'negative'} />
              <Metric label="Unrealised P/L" value={`${summary.unrealisedProfitLoss >= 0 ? '+' : ''}${formatCurrency(summary.unrealisedProfitLoss)}`} tone={summary.unrealisedProfitLoss >= 0 ? 'positive' : 'negative'} />
              <Metric label="Return" value={`${returnPercent >= 0 ? '+' : ''}${returnPercent.toFixed(1)}%`} tone={returnPercent >= 0 ? 'positive' : 'negative'} />
              <Metric label="Holdings" value={String(summary.holdings.length)} />
            </div>
          </section>
        )}

        {summary && summary.holdings.length > 0 && (
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold">Holdings</h2>
            <div className="space-y-3">
              {summary.holdings.map((holding) => (
                <div key={`${holding.assetType}-${holding.symbol ?? holding.assetName}`} className="rounded-xl bg-muted/30 p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{holding.assetName}</div>
                      <div className="text-sm text-muted-foreground">{holding.symbol || holding.assetType.replaceAll('_', ' ')}</div>
                    </div>
                    <span className="rounded-full bg-background px-3 py-1 text-xs font-semibold text-muted-foreground">
                      {holding.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Detail label="Qty" value={holding.quantityHeld.toLocaleString('en-IN')} />
                    <Detail label="Avg buy" value={formatCurrency(holding.averageBuyPrice)} />
                    <Detail label="Market" value={formatCurrency(holding.marketPrice)} />
                    <Detail label="Value" value={formatCurrency(holding.currentValue)} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-lg font-semibold">Transactions</h2>
          </div>
          {trades.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">No portfolio transactions found.</div>
          ) : (
            <div className="divide-y divide-border">
              {trades.map((trade) => (
                <div key={trade.id} className="flex items-center gap-4 px-5 py-4">
                  <div className={`rounded-full p-3 ${trade.tradeType === 'BUY' ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}>
                    {trade.tradeType === 'BUY' ? <FaArrowDown className="h-4 w-4" /> : <FaArrowUp className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{trade.assetName}</div>
                    <div className="text-sm text-muted-foreground">
                      {trade.tradeType} · {trade.quantity.toLocaleString('en-IN')} @ {formatCurrency(trade.price)} · {formatDate(trade.tradeDate)}
                    </div>
                  </div>
                  <div className="text-right font-semibold">
                    {formatCurrency(trade.quantity * trade.price + (trade.tradeType === 'BUY' ? trade.fees : -trade.fees))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </ScreenContainer>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'positive' | 'negative' }) {
  return (
    <div className="rounded-xl bg-muted/30 p-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-1 text-base font-semibold ${tone === 'positive' ? 'text-success' : tone === 'negative' ? 'text-error' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

export default PortfolioAccountDetailsScreen;
